<script setup lang="ts">
import { computed, ref } from "vue";

import type { Preset } from "@/backend/natsstubs/onboarding/models";
import BaseButton from "@/components/ui/BaseButton/BaseButton.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";

defineProps<{ presets: Preset[]; isLoading: boolean }>();
const emit = defineEmits<{
  /** Emitted when user clicks continue after selecting a preset */
  selectPreset: [presetId: string];
  /** Emitted when user clicks on a preset card */
  presetClicked: [presetId: string];
}>();

const selectedPresetId = ref<string | null>(null);

const canContinue = computed(() => !!selectedPresetId.value);

function selectPreset(id: string) {
  selectedPresetId.value = id;
  emit("presetClicked", id);
}

function handleCreate() {
  if (selectedPresetId.value) {
    emit("selectPreset", selectedPresetId.value);
  }
}
</script>

<template>
  <div class="v1-wrapper">
    <div class="v1-card">
      <h1 class="v1-title">{{ $t("onboarding.setupLearningSession") }}</h1>
      <p class="v1-subtitle">{{ $t("onboarding.demoDescription") }}</p>

      <div v-if="isLoading" class="v1-loading" aria-live="polite" aria-busy="true">
        {{ $t("onboarding.loadingTemplates") }}
      </div>

      <template v-else>
        <div class="v1-presets">
          <div class="v1-chips">
            <button
              v-for="preset in presets"
              :key="preset.id"
              type="button"
              class="v1-chip"
              :class="{ selected: selectedPresetId === preset.id }"
              @click="selectPreset(preset.id)"
            >
              <span class="v1-chip-label">{{ preset.name }}</span>
              <SvgIcon
                v-if="selectedPresetId === preset.id"
                name="system/check"
                class="v1-chip-check"
                width="18"
                height="18"
              />
            </button>
          </div>
        </div>

        <div class="v1-actions">
          <BaseButton variant="filled" :disabled="!canContinue" @click="handleCreate">
            {{ $t("loginPageEmail.continue") }}
          </BaseButton>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";
@use "@/styles/mixins/shadow";

.v1-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 40px;
  background: colors.$background-grey-light;
}

.v1-card {
  background: colors.$background-ui;
  border-radius: 12px;
  padding: 40px;

  @include shadow.default;

  max-width: 616px;
  width: 100%;
}

.v1-title {
  @include typography.extra-large;

  margin: 0 0 8px;
  text-align: center;
}

.v1-subtitle {
  @include typography.medium("regular");

  color: colors.$text-subtle;
  margin: 0 0 24px;
  text-align: center;
}

.v1-loading {
  @include typography.medium("regular");

  color: colors.$text-subtle;
  text-align: center;
  padding: 40px;
}

.v1-presets {
  margin-bottom: 24px;
}

.v1-chips {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.v1-chip {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid colors.$border-subtle;
  border-radius: 8px;
  background: colors.$background-ui;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    border-color: colors.$border;
    background: colors.$background-grey-light;
  }

  &.selected {
    border-color: colors.$main;
    background: rgba(colors.$main, 0.05);
  }
}

.v1-chip-label {
  @include typography.medium("semi-bold");

  flex: 1;
}

.v1-chip-check {
  color: colors.$main;
  flex-shrink: 0;
}

.v1-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
